import equal from "@superblocksteam/fast-deep-equal/es6";
import { Dimension, Padding } from "@superblocksteam/shared";
import { isEmpty } from "lodash";
import { createSelector } from "reselect";
import { fastClone } from "core/utils/clone";
import CanvasWidgetsNormalizer from "legacy/normalizers/CanvasWidgetsNormalizer";
import { APP_MODE } from "legacy/reducers/types";
import { getWidgets } from "legacy/selectors/entitiesSelector";
import { selectFlags } from "store/slices/featureFlags/selectors";
import { isDev } from "utils/env";
import logger from "utils/logger";

import {
  GridDefaults,
  WidgetTypes,
  PAGE_WIDGET_ID,
} from "../constants/WidgetConstants";
import { getAppMode, getResponsiveCanvasWidth } from "./applicationSelectors";
import { createMarkedSelector } from "./markedSelector";
import { getPageDSLVersion } from "./sagaSelectors";
import { selectGeneratedTheme } from "./themeSelectors";
import type { Hierarchy } from "../constants/WidgetConstants";
import type { BranchDto } from "@superblocksteam/shared";
import type { PageListReduxState } from "legacy/reducers/entityReducers/pageListReducer";
import type { DynamicWidgetsLayoutState } from "legacy/reducers/evaluationReducers/dynamicLayoutReducer";
import type { DynamicVisibilityProperties } from "legacy/widgets/BaseWidget";
import type { WidgetLayoutProps } from "legacy/widgets/shared";
import type { AppState } from "store/types";

type DynamicWidgetsVisibilityState = Record<
  string,
  DynamicVisibilityProperties
>;

const getDynamicLayoutWidgets = (
  _state: AppState,
): DynamicWidgetsLayoutState => ({});

const getDynamicVisibilityWidgets = (
  _state: AppState,
): DynamicWidgetsVisibilityState => ({});

const getResizingStackWidgetWidth = (
  _state: AppState,
): { canvasId: string; width: number } | undefined => undefined;

function applyStaticProps({
  target,
}: {
  target: Hierarchy | WidgetLayoutProps;
  [key: string]: unknown;
}): WidgetLayoutProps {
  return target as WidgetLayoutProps;
}

export const isLocalDevModeEnabled = (_state: AppState) => false;

export const getCurrentBranch = (_state: AppState): BranchDto | undefined =>
  undefined;

export const getEditorReadOnly = (state: AppState) => {
  if (getIsPageLoadingError(state)) return true;
  return getCurrentBranch(state)?.protected ?? false;
};

export const getIsFetchingPage = (_state: AppState) => false;

export const getIsPageLoadingError = (_state: AppState) => false;

export const getPageList = createSelector(
  (state: AppState) => state.legacy.entities.pageList.pages,
  (pages) =>
    [...pages].sort((page1, page2) =>
      page1.pageName.localeCompare(page2.pageName),
    ),
);

export const getCurrentPageId = (state: AppState) =>
  state.legacy.entities.pageList.currentPageId;

export const getCurrentApplicationId = (state: AppState) =>
  state.legacy.entities.pageList.applicationId;

export const getCurrentPageName = createMarkedSelector("getCurrentPageName")(
  (state: AppState) => state.legacy.entities.pageList,
  (pageList: PageListReduxState) =>
    pageList.pages.find((page) => page.pageId === pageList.currentPageId)
      ?.pageName,
);

export const getMainContainerWidgetId = createMarkedSelector(
  "getMainContainerWidgetId",
)(
  getPageDSLVersion,
  getWidgets,
  selectFlags,
  (dslVersion, widgets, flags): string => {
    return PAGE_WIDGET_ID;
  },
);

// This selector is used to get the DSL skeleton(widget hierarchy) of the current page
// instead of returning the entire DSL, we return only the skeleton to allow
// result equality checks to be more efficient
const getDSLHierarchy = createMarkedSelector("getDSLSkeleton", {
  resultEqualityCheck: equal,
})(getWidgets, (widgets): Hierarchy | undefined => {
  const denormalizedWidgets = CanvasWidgetsNormalizer.denormalize(
    PAGE_WIDGET_ID,
    {
      canvasWidgets: widgets,
    },
  ) as unknown as Hierarchy;
  if (!denormalizedWidgets) return undefined;

  const toSkeleton = (widget: Hierarchy): Hierarchy => {
    return {
      widgetId: widget?.widgetId,
      type: widget?.type,
      widgetName: widget?.widgetName,
      children: widget?.children?.map((c) => toSkeleton(c)),
    };
  };
  return toSkeleton(denormalizedWidgets);
});

export const gridDescendants = createMarkedSelector("gridDescendants")(
  getDSLHierarchy,
  (dsl) => {
    const descendants = new Set<string>();
    const traverse = (widget: Hierarchy, parentIsGrid: boolean) => {
      if (parentIsGrid) {
        descendants.add(widget.widgetId);
      }

      if (widget.type === WidgetTypes.GRID_WIDGET) {
        widget.children?.forEach((child) => {
          traverse(child, true);
        });
      } else if (
        widget.type === WidgetTypes.MODAL_WIDGET ||
        widget.type === WidgetTypes.SLIDEOUT_WIDGET
      ) {
        // These widgets are not part of the grid layout because they are detached
        widget.children?.forEach((child) => {
          traverse(child, false);
        });
      } else {
        widget.children?.forEach((child) => {
          traverse(child, parentIsGrid);
        });
      }
    };
    if (dsl) {
      traverse(dsl, false);
    }
    return descendants;
  },
);

// These are used to cache the previous result of @getStaticStructure
const staticStructureCache = {
  widgets: {} as ReturnType<typeof getWidgets>,
  skeleton: undefined as ReturnType<typeof getDSLHierarchy>,
  width: 0 as ReturnType<typeof getResponsiveCanvasWidth>,
  theme: undefined as ReturnType<typeof selectGeneratedTheme> | undefined,
  dynamicWidgets: {} as ReturnType<typeof getDynamicLayoutWidgets>,
  dynamicVisibilityWidgets: {} as ReturnType<
    typeof getDynamicVisibilityWidgets
  >,
  clearCache: () => {
    staticStructureCache.skeleton = undefined;
    staticStructureCache.widgets = {};
    staticStructureCache.width = 0;
    staticStructureCache.theme = undefined;
    staticStructureCache.dynamicWidgets = {};
    staticStructureCache.dynamicVisibilityWidgets = {};
  },
};

export const getStaticStructure = createMarkedSelector("getStaticStructure")(
  getDSLHierarchy,
  getWidgets,
  getResponsiveCanvasWidth,
  selectGeneratedTheme,
  selectFlags,
  getDynamicLayoutWidgets,
  getDynamicVisibilityWidgets,
  getResizingStackWidgetWidth,
  getAppMode,
  (
    skeleton,
    widgets,
    width,
    theme,
    flags,
    _dynamicLayoutWidgets,
    dynamicVisibilityWidgets,
    resizingStackWidget,
    appMode,
  ): WidgetLayoutProps | undefined => {
    const previousResult = getStaticStructure.lastResult() as WidgetLayoutProps;
    if (skeleton === undefined || isEmpty(widgets)) {
      return undefined;
    }
    let dynamicLayoutWidgets = _dynamicLayoutWidgets;
    if (
      appMode === APP_MODE.EDIT &&
      resizingStackWidget?.canvasId &&
      widgets[resizingStackWidget.canvasId]?.type ===
        WidgetTypes.CANVAS_WIDGET &&
      resizingStackWidget?.width
    ) {
      dynamicLayoutWidgets = {
        ..._dynamicLayoutWidgets,
        [resizingStackWidget.canvasId]: {
          ..._dynamicLayoutWidgets[resizingStackWidget.canvasId],
          width: Dimension.px(resizingStackWidget.width),
        },
      };
    }

    // if the width or skeleton has changed, we need to recalculate
    const needsNewObject =
      staticStructureCache.skeleton !== skeleton ||
      staticStructureCache.theme !== theme ||
      staticStructureCache.width !== width;

    const target = needsNewObject ? fastClone(skeleton) : { ...previousResult };

    if (isDev() && isEmpty(target)) {
      logger.error(
        `target is empty, skeleton:${JSON.stringify(skeleton)}
        previousResult: ${JSON.stringify(previousResult)}`,
      );
    }

    const denormalizedWidgets = applyStaticProps({
      sWidgets: widgets,
      cachedSWidgets: staticStructureCache.widgets,
      eWidgets: dynamicVisibilityWidgets,
      cachedEWidgets: staticStructureCache.dynamicVisibilityWidgets,
      dWidgets: dynamicLayoutWidgets,
      cachedDWidgets: staticStructureCache.dynamicWidgets,

      target,
      isTargetNew: needsNewObject,
    });

    if (denormalizedWidgets) {
      const xPadding = Padding.x(denormalizedWidgets.padding).value;

      denormalizedWidgets.parentColumnSpace =
        (width - xPadding) / GridDefaults.DEFAULT_GRID_COLUMNS;
      denormalizedWidgets.width = Dimension.px(width - xPadding);
      denormalizedWidgets.left = Dimension.gridUnit(0);
      denormalizedWidgets.gridColumns = GridDefaults.DEFAULT_GRID_COLUMNS;
    }
    staticStructureCache.widgets = { ...widgets };
    staticStructureCache.dynamicVisibilityWidgets = {
      ...dynamicVisibilityWidgets,
    };
    staticStructureCache.dynamicWidgets = { ...dynamicLayoutWidgets };
    staticStructureCache.skeleton = skeleton;
    staticStructureCache.theme = theme;
    staticStructureCache.width = width;
    return denormalizedWidgets;
  },
);

export const getIsLeftPanePinned = (_state: AppState) => false;
